import React from 'react';

import { DateRangePicker } from '@heroui/react';
import type { RangeValue } from '@react-types/shared';
import { CalendarDate, DateValue, getLocalTimeZone } from '@internationalized/date';
import { RiCalendarLine } from '@remixicon/react';

interface DateRangeFilterProps {
  label?: string;
  value?: { start: Date | null; end: Date | null };
  onChange?: (value: { start: Date | null; end: Date | null }) => void;
  containerClass?: string;
}

export function DateRangeFilter({
  label = 'Updated Date',
  value = { start: null, end: null },
  onChange,
  containerClass = ''
}: DateRangeFilterProps) {
  const toCalendarDate = React.useCallback((date: Date) => {
    return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
  }, []);

  const toJsDate = React.useCallback((dateValue: DateValue | null) => {
    if (!dateValue) return null;
    if ('toDate' in dateValue && typeof dateValue.toDate === 'function') {
      return dateValue.toDate(getLocalTimeZone());
    }

    const calendarDate = dateValue as CalendarDate;
    return new Date(calendarDate.year, calendarDate.month - 1, calendarDate.day);
  }, []);

  const pickerValue = React.useMemo<RangeValue<DateValue> | null>(() => {
    if (!value?.start && !value?.end) {
      return null;
    }

    const start = value?.start ? toCalendarDate(value.start) : undefined;
    const end = value?.end ? toCalendarDate(value.end) : undefined;

    if (!start && !end) {
      return null;
    }

    if (!start && end) {
      return { start: end, end };
    }

    return {
      start: start!,
      end: end ?? start!
    };
  }, [value?.start, value?.end, toCalendarDate]);

  const handleChange = React.useCallback(
    (val: RangeValue<DateValue> | null) => {
      if (!onChange) return;

      if (!val) {
        onChange({ start: null, end: null });
        return;
      }

      onChange({
        start: toJsDate(val.start),
        end: toJsDate(val.end)
      });
    },
    [onChange, toJsDate]
  );

  return (
    <div className={`flex flex-col gap-2 ${containerClass}`}>
      <DateRangePicker
        value={pickerValue}
        onChange={handleChange}
        size="sm"
        className="max-w-xs"
        label={label}
        startContent={<RiCalendarLine className="text-default-400 h-4 w-4" />}
      />
    </div>
  );
}
